import EmojiPicker from "emoji-picker-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, FloatingLabel, Form, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Assets } from "../../../../constants/images";
import {
  recipient_types,
  recurring_at_this_time_array,
  task_list_day_short,
  task_list_due_days_array,
} from "../../../../utils/constants";
import { GetAuthUserLocalStorage } from "../../../../services/localStorage/localStorage";
import CustomSelect from "../../../FormFields/CustomSelect";
import CustomTreeSelect from "../../../FormFields/CustomTreeSelect";

const EMPTY_TASK_ROWS = ["", "", ""];

const CircleMinus = () => (
  <svg width="24" height="24" viewBox="0 0 640 640" fill="none">
    <path
      fill="#CFCFCF"
      d="M320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320C64 461.4 178.6 576 320 576zM232 296L408 296C421.3 296 432 306.7 432 320C432 333.3 421.3 344 408 344L232 344C218.7 344 208 333.3 208 320C208 306.7 218.7 296 232 296z"
    />
  </svg>
);

const CirclePlus = () => (
  <svg width="24" height="24" viewBox="0 0 640 640" fill="none">
    <path
      fill="#CFCFCF"
      d="M320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320C64 461.4 178.6 576 320 576zM296 408L296 344L232 344C218.7 344 208 333.3 208 320C208 306.7 218.7 296 232 296L296 296L296 232C296 218.7 306.7 208 320 208C333.3 208 344 218.7 344 232L344 296L408 296C421.3 296 432 306.7 432 320C432 333.3 421.3 344 408 344L344 344L344 408C344 421.3 333.3 432 320 432C306.7 432 296 421.3 296 408z"
    />
  </svg>
);

const deriveCategory = (recipientType) => {
  if (recipientType == recipient_types?.everyone) return "everyone";
  if (recipientType == recipient_types?.group) return "group";
  if (recipientType == recipient_types?.member) return "member";
  return null;
};

const TaskListModalForm = (props) => {
  const { isEdit, selectedList, show, setShow, onSaved } = props;
  const authUser = GetAuthUserLocalStorage();
  const { salonTeams } = useSelector((state) => state?.salon_teams);
  const { currentSalonId } = useSelector((state) => state.global);

  const [name, setName] = useState("");
  const [assignees, setAssignees] = useState([]); // selected ids (users OR groups) as strings
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [recipientType, setRecipientType] = useState("");
  const [dueDays, setDueDays] = useState([]);
  const [dueTime, setDueTime] = useState(null);
  const [taskRows, setTaskRows] = useState([...EMPTY_TASK_ROWS]);
  const [disable, setDisable] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [staffMenuOpen, setStaffMenuOpen] = useState(false);
  const emojiPickerRef = useRef(null);

  const resetForm = () => {
    setName("");
    setAssignees([]);
    setSelectedCategory(null);
    setRecipientType("");
    setDueDays([]);
    setDueTime(null);
    setTaskRows([...EMPTY_TASK_ROWS]);
    setShowPicker(false);
  };

  // Hydrate on open (edit) / reset on close.
  useEffect(() => {
    if (!show) {
      resetForm();
      setStaffMenuOpen(false);
      return;
    }
    if (isEdit && selectedList) {
      setName(selectedList?.name || "");
      const category = deriveCategory(selectedList?.recipient_type);
      setSelectedCategory(category);
      setRecipientType(selectedList?.recipient_type || "");
      // Tree preselection: groups show group ids, everyone/member show user ids.
      const preselected =
        category === "group"
          ? (selectedList?.recipients || []).map((id) => id?.toString())
          : (selectedList?.assignees || []).map((id) => id?.toString());
      setAssignees(preselected);
      setDueDays((selectedList?.due_on || []).map((day) => Number(day)));
      setDueTime(selectedList?.due_time || null);
      const items = (selectedList?.items || []).map((it) => it?.name || "");
      setTaskRows(
        items.length >= 2 ? items : [...items, ...EMPTY_TASK_ROWS].slice(0, 3),
      );
    } else {
      resetForm();
    }
  }, [show, isEdit, selectedList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        showPicker
      ) {
        setShowPicker(false);
      }
    };
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, [showPicker]);

  const handleTreeSelectValue = (parentNode, value) => {
    setAssignees(value || []);
    setSelectedCategory(parentNode);
    setRecipientType(
      parentNode == "everyone"
        ? recipient_types?.everyone
        : parentNode == "group"
          ? recipient_types?.group
          : recipient_types?.member,
    );
  };

  // Expand tree selection into user ids + group ids for the API payload.
  const returnUsers = () => {
    if (selectedCategory === "group") {
      const groups = salonTeams?.data?.groups?.filter((item) =>
        assignees?.includes(item?.id?.toString()),
      );
      let users = [];
      let groupsData = [];
      (groups || []).forEach((g) => {
        groupsData.push(Number(g?.id));
        (g?.users || []).forEach((u) => users.push(Number(u?.id)));
      });
      return { usersData: [...new Set(users)], groupsData };
    }
    return {
      usersData: [...new Set((assignees || []).map((id) => Number(id)))],
      groupsData: [],
    };
  };

  const toggleDay = (day) => {
    const value = Number(day);
    setDueDays((prev) => {
      const current = (prev || []).map(Number);
      return current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value].sort((a, b) => a - b);
    });
  };

  const addTaskRow = () => setTaskRows((rows) => [...rows, ""]);

  const removeTaskRow = (index) => {
    setTaskRows((rows) => {
      if (rows.length <= 2) return rows;
      const next = [...rows];
      next.splice(index, 1);
      return next;
    });
  };

  const updateTaskRow = (index, value) => {
    setTaskRows((rows) => {
      const next = [...rows];
      next[index] = value;
      return next;
    });
  };

  const namedTasks = useMemo(
    () => taskRows.map((t) => (t || "").trim()).filter(Boolean),
    [taskRows],
  );

  const canSave = useMemo(() => {
    return (
      name.trim().length > 0 &&
      dueDays.length > 0 &&
      !!dueTime &&
      namedTasks.length >= 2 &&
      (assignees?.length || 0) > 0
    );
  }, [name, dueDays, dueTime, namedTasks, assignees]);

  const handleSave = async () => {
    if (!canSave || disable) return;
    const { usersData, groupsData } = returnUsers();
    const apiData = {
      name: name.trim(),
      due_time: dueTime,
      due_on: dueDays,
      items: namedTasks,
      assignees: usersData,
      ...(selectedCategory === "group" && { recipients: groupsData }),
      recipient_type: recipientType,
      assign_by: authUser?.id,
      salon_id: currentSalonId,
    };
    // Lazy import to avoid a circular import chain at module load.
    const { saveTaskList } = await import(
      "../../../../services/tasks/frontend-tasks"
    );
    await saveTaskList(
      apiData,
      isEdit,
      selectedList?.id,
      setDisable,
      setShow,
      resetForm,
      onSaved,
    );
  };

  const handleEmojiClick = (emojiData) => {
    setName((prev) => prev + emojiData.emoji);
  };

  return (
    <div
      className={`taskListForm${staffMenuOpen ? " is-staff-menu-open" : ""}`}
    >
      <div className="taskListForm-body customNewTaskModalBody">
        <Row>
          <Col xs={12}>
            <div className="taskListForm-split">
              <div className="taskListForm-splitMain">
                <FloatingLabel
                  label="Task List Name"
                  className="mb-20 custom-textarea"
                >
                  <Form.Control
                    placeholder="ex. Opening Salon Duties"
                    minLength={1}
                    maxLength={100}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <div ref={emojiPickerRef}>
                    <div
                      onClick={() => setShowPicker(!showPicker)}
                      className={"smileyBox"}
                    >
                      <img src={Assets.Smile} className={"smileImage"} />
                    </div>
                    {showPicker && (
                      <EmojiPicker onEmojiClick={handleEmojiClick} />
                    )}
                  </div>
                </FloatingLabel>
              </div>
              <div className="taskListForm-splitSide">
                <CustomTreeSelect
                  label="Assign Staff"
                  placeholder="Assign Staff"
                  defaultSelectedData={assignees}
                  handleChange={handleTreeSelectValue}
                  selectedCategory={selectedCategory}
                  isTaskDialog={true}
                  onOpenChange={setStaffMenuOpen}
                />
              </div>
            </div>

            <div className="taskListForm-split taskListForm-schedule">
              <div
                className="taskListForm-splitMain taskListForm-days"
                role="group"
                aria-label="Days of the week"
              >
                {task_list_due_days_array.map((day) => {
                  const checked = dueDays
                    .map(Number)
                    .includes(Number(day.value));
                  return (
                    <label
                      key={day.value}
                      className={`taskListForm-day${checked ? " is-checked" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleDay(day.value)}
                      />
                      <span
                        className="taskListForm-dayMark"
                        aria-hidden="true"
                      />
                      <span>{task_list_day_short[day.value]}</span>
                    </label>
                  );
                })}
              </div>
              <div className="taskListForm-splitSide">
                <CustomSelect
                  placeholder={"Start Time"}
                  label={"Start Time"}
                  value={dueTime}
                  options={recurring_at_this_time_array}
                  onChange={(value) => setDueTime(value)}
                />
              </div>
            </div>
          </Col>

          <Col xs={12}>
            {taskRows.map((row, index) => {
              const isLast = index === taskRows.length - 1;
              // The first two tasks are required and cannot be removed.
              const canRemove = index >= 2;
              return (
                <div className="taskListForm-taskRow" key={index}>
                  <FloatingLabel
                    label={`Task #${index + 1}`}
                    className="mb-20 custom-textarea taskListForm-taskInput"
                  >
                    <Form.Control
                      placeholder={`Task #${index + 1}`}
                      maxLength={255}
                      value={row}
                      onChange={(e) => updateTaskRow(index, e.target.value)}
                    />
                    <div className="taskListForm-taskActions">
                      {canRemove && (
                        <button
                          type="button"
                          className="taskListForm-iconBtn"
                          onClick={() => removeTaskRow(index)}
                          aria-label="Remove task"
                        >
                          <CircleMinus />
                        </button>
                      )}
                      {isLast && (
                        <button
                          type="button"
                          className="taskListForm-iconBtn"
                          onClick={addTaskRow}
                          aria-label="Add task"
                        >
                          <CirclePlus />
                        </button>
                      )}
                    </div>
                  </FloatingLabel>
                </div>
              );
            })}
          </Col>
        </Row>
      </div>

      <div className="modal-footer">
        <div className="custom-footer-btns w-100">
          <div>
            <Button
              type="button"
              className={"btn-gray custom-width-332 margin-right-26"}
              onClick={() => setShow(false)}
            >
              Back
            </Button>
          </div>
          <div>
            <Button
              type="button"
              className={"custom-width-332"}
              disabled={!canSave || disable}
              onClick={handleSave}
            >
              {disable ? (
                <div className="load black" />
              ) : canSave ? (
                "Save & Assign"
              ) : (
                "Save List"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskListModalForm;
